// src/app/core/services/auth.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { environment } from '@env/environment';
import { Api } from './api';
import { AuthResult, LoginDto, User } from '@core/models/user.model';

@Injectable({ providedIn: 'root' })
export class Auth {
  private readonly TOKEN_KEY = environment.jwtStorageKey ?? 'token';
  private currentTokenSubject = new BehaviorSubject<string | null>(this.getTokenFromStorage());
  public currentToken$ = this.currentTokenSubject.asObservable();
  // optionally keep a parsed user
  private currentUserSubject = new BehaviorSubject<User | null>(this.parseUserFromToken(this.getTokenFromStorage()));
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private api: Api) { }

  login(dto: LoginDto): Observable<AuthResult> {
    // POST to your API's login: e.g. /auth/login or /login depending on server
    return this.api.post<AuthResult>('Auth/login', dto).pipe(
      map(result => {
        if (result && (result.token || (result as any).accessToken)) {
          const token = (result as any).token ?? (result as any).accessToken;
          this.setToken(token);
        }
        return result;
      })
    );
  }

  logout() {
    this.clearToken();
    // If your API supports server-side logout, call it here
  }

  isAuthenticated(): boolean {
    return !!this.currentTokenSubject.value;
  }

  getToken(): string | null {
    return localStorage.getItem('token'); // or environment.jwtStorageKey
  }

  private setToken(token: string) {
    localStorage.setItem(this.TOKEN_KEY, token);
    this.currentTokenSubject.next(token);
    this.currentUserSubject.next(this.parseUserFromToken(token));
  }

  private clearToken() {
    localStorage.removeItem(this.TOKEN_KEY);
    this.currentTokenSubject.next(null);
    this.currentUserSubject.next(null);
  }

  private getTokenFromStorage(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  private parseUserFromToken(token: string | null): User | null {
    if (!token) return null;
    try {
      // naive JWT decode (base64 payload)
      const payload = token.split('.')[1];
      if (!payload) return null;
      const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const obj = JSON.parse(decodeURIComponent(escape(json)));
      // adapt keys to your token payload
      return {
        id: obj.sub ?? obj.id,
        username: obj.unique_name ?? obj.name ?? obj.username,
        roles: obj.role ? (Array.isArray(obj.role) ? obj.role : [obj.role]) : []
      };
    } catch {
      return null;
    }
  }
}
